import { Fragment, useMemo } from 'react';
import { Card, Table, Badge, ProgressBar } from 'react-bootstrap';

import { buildLaunchPreviewGroups } from '../../../helpers/launchPreview';
import { buildBlockRows, getPrimaryOutput } from '../../../helpers/machineQueueBlocks';
// The panel's own three rules, kept as data so they can be checked against the
// panel itself — see tests/launchPreviewRender.test.js.
import {
  deliveryOf,
  executionText,
  materialLines,
  wireDiameterOf,
  fmtMeters as fmt
} from '../../../helpers/launchPreviewDisplay';

// The launch, as the machine will show it.
//
// Final Production Review listed each product's executions in its own little
// table — the wizard's shape. The operator panel (`:5005/<machine>-hmi`) shows
// ONE list per machine, in the order it will be run, with a merged drum as a
// single row and the products of a lot named once at the top joined by a `+`:
//
//     CYYF 4x2.5          CYYF 3x2.5
//     LOT: 10 000 m   +   LOT: 10 000 m
//
// That difference is largest on exactly the launches that are hardest to check
// before pressing Submit:
//
//   · a GROUP-BY-COLOUR run, where the drums of two products interleave colour
//     by colour (blue, blue, brown, brown…) because a colour change on an
//     extruder costs a purge — not three drums of one product then four of the
//     other, which is what this page used to show;
//   · a MERGED phase, where several products' shares land on one drum;
//   · a MERGED or GROUPED unwinding, where the pairing is by wire Ø and not by
//     colour at all, so the Ø has to be on screen.
//
// The grouping is NOT re-implemented here — see helpers/launchPreview.js. This
// renders what `buildBlockRows` returns, which is the same function the queue
// page uses, fed from wizard state instead of from a queue.

const PHASE_LABEL = {
  unwinding: 'DERULARE',
  pre_stranding: 'PRE-TORSADARE',
  conductor_isolation: 'IZOLARE',
  rope: 'TORSADARE',
  interior_sheat: 'MANTA INT.',
  armouring: 'ARMARE',
  exterior_sheat: 'MANTA EXT.',
  coiling: 'COLACI',
  rewinding: 'REBOBINARE'
};

const COLOR_HEX = {
  BLUE: '#2f7cff',
  BROWN: '#8b5a2b',
  BLACK: '#222222',
  GREY: '#7a7a7a',
  GRAY: '#7a7a7a',
  'GREEN-YELLOW': '#9acd32',
  GREEN: '#2e8b57',
  YELLOW: '#f0c400',
  RED: '#d9534f',
  WHITE: '#e5e5e5',
  ORANGE: '#f39c12',
  NATUR: '#e8e2d4',
  SDR: '#555c6e',
  GRI: '#7a7a7a'
};
const colorHex = c => COLOR_HEX[String(c || '').toUpperCase()] || null;

/** A small round swatch — the colour of the conductor or of the compound. */
const Dot = ({ color, size = 12, title }) => {
  const hex = colorHex(color);
  if (!hex) return null;
  return (
    <span
      title={title || color}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: hex,
        border: '1px solid rgba(255,255,255,0.25)',
        display: 'inline-block',
        flex: '0 0 auto'
      }}
    />
  );
};

function drumText(block) {
  const first = block?.items?.[0]?.task || {};
  const out = getPrimaryOutput(first) || {};
  const material = String(out.drum_material || first.drum_material || '').toUpperCase();
  const diameter = out.drum_diameter || first.drum_diameter || null;
  if (!material && !diameter) return <span className="text-700">—</span>;
  return (
    <>
      <div className="fw-medium">{material || '—'}</div>
      {diameter ? <div className="fs-10 text-700">Ø {fmt(diameter)}</div> : null}
    </>
  );
}

/** The lot header: every product of the run, joined by a big `+`. */
const LotHeader = ({ products, total }) => (
  <tr>
    <td colSpan={8} className="bg-body-tertiary py-2 border-top border-200">
      <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
        {products.map((p, i) => (
          <div key={p.key || p.jobId || i} className="d-flex align-items-center gap-2">
            {i > 0 && (
              <span className="fw-bold text-primary px-2" style={{ fontSize: 24, lineHeight: 1 }}>
                +
              </span>
            )}
            <div className="text-center" style={{ minWidth: 150 }}>
              <div className="fw-bold">{p.productLabel || '—'}</div>
              {/* LOT is per product, so a group-by run that shares one header
                  states each one — the panel does the same. */}
              <div className="fs-10 text-700 font-monospace">LOT: {fmt(p.plannedLength)} m</div>
            </div>
          </div>
        ))}
        {products.length > 1 && Number.isFinite(total) && (
          <Badge variant="phoenix" bg="secondary" className="fs-10 ms-2">
            {products.length} produse · {fmt(total)} m
          </Badge>
        )}
      </div>
    </td>
  </tr>
);

/**
 * The heading of a group-by bucket: what this stretch of the queue has in
 * common. Without it a grouped run is just rows in a surprising order.
 */
const GroupHeader = ({ groupBy, count }) => {
  const isColor = groupBy.kind === 'color';
  return (
    <tr>
      <td colSpan={8} className="py-1" style={{ background: 'rgba(127,127,127,0.07)' }}>
        <div className="d-flex align-items-center gap-2 fs-10">
          {isColor ? (
            <>
              <Dot color={groupBy.value} size={14} />
              <span className="fw-bold text-uppercase">{groupBy.value || 'FARA CULOARE'}</span>
              <span className="text-700">· grupat pe culoare</span>
            </>
          ) : (
            <>
              <span className="fw-bold font-monospace">
                Ø {groupBy.value === 'NA' ? '—' : groupBy.value} mm
              </span>
              <span className="text-700">· grupat pe diametru sârmă</span>
            </>
          )}
          <span className="ms-auto text-700">
            {count} {count === 1 ? 'tambur' : 'tamburi'}
          </span>
        </div>
      </td>
    </tr>
  );
};

const MachineProgramPreview = ({
  standardData = [],
  mergedPhases = {},
  groupByPhases = {},
  orderedPhases = [],
  labelOf
}) => {
  // One table per phase, because one phase is one machine's program.
  const perPhase = useMemo(
    () =>
      (orderedPhases || [])
        .map(phaseName => ({
          phaseName,
          rows: buildBlockRows(
            buildLaunchPreviewGroups({
              standardData,
              mergedPhases,
              groupByPhases,
              phaseName,
              ...(labelOf ? { labelOf } : {})
            })
          )
        }))
        .filter(x => x.rows.length),
    [standardData, mergedPhases, groupByPhases, orderedPhases, labelOf]
  );

  if (!perPhase.length) return null;

  const today = new Date().toLocaleDateString('ro-RO');

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex align-items-center gap-2 mb-1">
          <h5 className="mb-0">Programul pe mașină</h5>
          <Badge bg="secondary" variant="phoenix" className="fs-10">
            preview
          </Badge>
        </div>
        <div className="fs-9 text-700 mb-3">
          Exact ce va lista panoul operatorului, construit din această lansare —
          un rând pe tambur, un tambur partajat ca un singur rând cu ambele cote,
          produsele unui lot numite o singură dată, și ordinea reală de intrare în
          mașină. Nu s-a scris încă nimic.
        </div>

        {perPhase.map(({ phaseName, rows }) => {
          const machines = [
            ...new Set(
              rows
                .flatMap(r => (r.block.items || []).map(it => it.task.machineId))
                .filter(Boolean)
                .map(m => String(m).toUpperCase())
            )
          ];
          const totalMeters = rows.reduce(
            (s, r) =>
              s + r.block.items.reduce((a, it) => a + (Number(it.task.plannedLength) || 0), 0),
            0
          );
          const sharedDrums = rows.filter(r => r.block.items.length > 1).length;
          const groupMode = groupByPhases?.[phaseName] || null;
          const diameters = [
            ...new Set(rows.map(r => wireDiameterOf(r.block.items[0]?.task)).filter(Boolean))
          ].sort((a, b) => a - b);

          let lastGroupKey = null;

          return (
            <div key={phaseName} className="mb-4">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="fw-bold fs-8">{PHASE_LABEL[phaseName] || phaseName}</span>

                {machines.map(m => (
                  <Badge key={m} bg="info" variant="phoenix" className="fs-10">
                    {m}
                  </Badge>
                ))}

                {mergedPhases?.[phaseName] && (
                  <Badge bg="warning" variant="phoenix" className="fs-10">
                    merge execution{sharedDrums ? ` · ${sharedDrums} partajate` : ''}
                  </Badge>
                )}

                {groupMode && (
                  <Badge bg="primary" variant="phoenix" className="fs-10">
                    group by {groupMode === 'color' ? 'culoare' : 'diametru'}
                  </Badge>
                )}

                {/* A merged or grouped unwinding pairs by wire Ø, so the Ø has to
                    be visible without opening a row. */}
                {diameters.length > 0 && (
                  <span className="fs-10 text-700 font-monospace">
                    Ø {diameters.map(d => d.toFixed(2)).join(' · ')} mm
                  </span>
                )}

                <span className="ms-auto fs-10 text-700">
                  {rows.length} {rows.length === 1 ? 'tambur' : 'tamburi'} ·{' '}
                  <span className="font-monospace">{fmt(totalMeters)} m</span>
                </span>
              </div>

              <Table responsive className="phoenix-table fs-9 mb-0">
                <thead>
                  <tr className="text-700">
                    <th style={{ width: 70 }}>Nr. / Data</th>
                    <th style={{ width: 150 }}>Faza</th>
                    <th>Input</th>
                    <th style={{ width: 150 }}>În execuție</th>
                    <th style={{ width: 120 }}>Executat</th>
                    <th style={{ width: 110 }}>Tambur</th>
                    <th style={{ width: 100 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const first = row.block.items[0]?.task || {};
                    const shared = row.block.items.length > 1;
                    const total = row.block.items.reduce(
                      (s, it) => s + (Number(it.task.plannedLength) || 0),
                      0
                    );
                    const lines = materialLines(first);
                    const delivery = deliveryOf(first.deliveryMode);

                    // A new group-by bucket gets its own little heading.
                    const groupKey = row.isGroupByRun && row.groupBy
                      ? `${row.groupBy.kind}:${row.groupBy.value}`
                      : null;
                    const showGroup = groupKey && groupKey !== lastGroupKey;
                    if (groupKey) lastGroupKey = groupKey;
                    const groupCount = groupKey
                      ? rows.filter(
                          r =>
                            r.isGroupByRun &&
                            r.groupBy &&
                            `${r.groupBy.kind}:${r.groupBy.value}` === groupKey
                        ).length
                      : 0;

                    // The lot total, for the header that is about to be drawn.
                    const lotTotal = rows
                      .filter(r => r.lotRunId === row.lotRunId)
                      .reduce(
                        (s, r) =>
                          s +
                          r.block.items.reduce(
                            (a, it) => a + (Number(it.task.plannedLength) || 0),
                            0
                          ),
                        0
                      );

                    return (
                      <Fragment key={row.id}>
                        {row.showJob && <LotHeader products={row.products} total={lotTotal} />}
                        {showGroup && <GroupHeader groupBy={row.groupBy} count={groupCount} />}

                        <tr style={{ borderLeft: `4px solid ${row.borderColor}` }}>
                          <td className="text-center">
                            <div className="fw-semibold">{i + 1}</div>
                            <div className="fs-10 text-700">{today}</div>
                          </td>

                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-medium">
                                {PHASE_LABEL[first.phase] || first.phase}
                              </span>
                              <Dot color={first.color} />
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                              <Badge bg={delivery.bg} variant="phoenix" className="fs-10">
                                {delivery.text}
                              </Badge>
                              {shared && (
                                <Badge bg="warning" variant="phoenix" className="fs-10">
                                  ⇄ {row.block.items.length} produse
                                </Badge>
                              )}
                            </div>
                            {first.color && (
                              <div className="fs-10 text-700 mt-1">{first.color}</div>
                            )}
                          </td>

                          <td className="fs-10 text-800">
                            {lines.map((line, k) => (
                              <div key={k} className="d-flex align-items-center gap-1">
                                {line.kind === 'compound' && <Dot color={line.color} size={9} />}
                                <span className={line.kind === 'copper' ? 'font-monospace' : ''}>
                                  {line.text}
                                </span>
                              </div>
                            ))}
                            {!lines.length && <span className="text-700">—</span>}
                          </td>

                          <td className="font-monospace fs-10 text-center">
                            {executionText(row.block)}
                            {shared && (
                              <div className="fs-10 text-700">
                                {row.block.items.length} cote pe un tambur
                              </div>
                            )}
                          </td>

                          <td className="text-center">
                            <div className="font-monospace fs-10">0 / {fmt(total)} m</div>
                            <ProgressBar
                              now={0}
                              style={{ height: 4 }}
                              className="bg-body-tertiary mt-1"
                            />
                          </td>

                          <td className="text-center fs-10">{drumText(row.block)}</td>

                          <td className="text-center">
                            <Badge bg="warning" variant="phoenix" className="fs-10">
                              PENDING
                            </Badge>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

export default MachineProgramPreview;
export { PHASE_LABEL };
